/* eslint-disable import/no-cycle */
import {
    Button,
    Card,
    CircularProgress,
    createStyles,
    FormControl,
    Grid,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    TextField,
} from '@material-ui/core';
import {Alert} from '@material-ui/lab';
import {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';
import {AxiosResponse} from 'axios';
import {RxRequest, RxInitialResponse} from '@amplify/athena-models';
import {runInAction} from 'mobx';
import API from '../../APIs/API';
import Utils from '../../Core/Utils';
import RootStore from '../../Core/mobx/RootStore';
import {observer} from 'mobx-react-lite';
import {RxTable} from './RxTable';
import {User} from '../../Auth/User';
import constants from '../../constants';
import CRM from '../../APIs/CRM';
import RxDialog from './RxDialog';
import {useFeatureIsOn} from '@growthbook/growthbook-react';
import {ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
const {CUSTOMER_PORTAL_URL} = constants;
const RX_FETCH_RETRY_WAIT_TIME = 5000;
const RX_RETRY_COUNT_LIMIT = 30;

const useStyles = makeStyles(() =>
    createStyles({
        formControl: {
            marginTop: 8,
            width: '100%',
        },
    }),
);

export type RxResponse = {status: string; message?: string; error?: string; data: PrescriptionResponse[]};
export type PrescriptionResponse = {rx: string; score: number; date: string; cond: string[]};
export type Prescription = {drug: string; score: number; date: string[]; cond?: Array<string>};
export type Physician = {
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    specialty: Array<string>;
};

export const RXPanel = observer(
    ({parameterStore, fullWidth = false}: {parameterStore: ParameterStore | null; fullWidth?: boolean}) => {
        const classes = useStyles();

        const [showConsentSuggestion, setShowConsentSuggestion] = useState(false);
        const [showConsentDialog, setShowConsentDialog] = useState(false);

        const [consentLink, setConsentLink] = useState('');
        const [consentEmail, setConsentEmail] = useState('');
        const [consentPhone, setConsentPhone] = useState('');
        const {historyFormId} = useParams<never>();

        const newRxFlow = useFeatureIsOn('new-rx-flow');

        type fieldName =
            | 'firstName'
            | 'lastName'
            | 'gender'
            | 'dob'
            | 'socialSecurityNumber'
            | 'addressZipCode'
            | 'byear'
            | 'phoneNumber'
            | 'emailAddress';

        const [validationDetails, setValidationDetails] = useState([
            {n: 'firstName', v: 'Lucy'},
            {n: 'lastName', v: 'Williams'},
            {n: 'gender', v: 'female'},
            {n: 'dob', v: '1948-04-08'},
            {n: 'socialSecurityNumber', v: '222222222'},
            {n: 'addressZipCode', v: '11787'},
        ]);

        const getParam = (detailName: fieldName): any => {
            if (parameterStore?.approval == null) {
                return validationDetails.find((detail) => detail.n == detailName)?.v;
            } else {
                if (detailName == 'dob') {
                    return (
                        parameterStore.approval.byear?.value +
                        '-' +
                        parameterStore.approval.bmonth?.value +
                        '-' +
                        parameterStore.approval.bday?.value
                    );
                } else {
                    return parameterStore.approval[detailName as keyof typeof parameterStore.approval]?.value;
                }
            }
        };

        const setParam = (detailName: fieldName, value: any) => {
            if (parameterStore?.approval == null) {
                const validationDetail = validationDetails.find((validationDetail) => validationDetail.n == detailName);
                if (validationDetail != null) {
                    validationDetail.v = value;
                }
                setValidationDetails(Utils.jsClone(validationDetails));
            } else {
                runInAction(() => {
                    const parameter = parameterStore.approval[detailName as keyof typeof parameterStore.approval];
                    if (parameter != null) {
                        parameter.value = value;
                    }
                });
            }
        };

        const [loading, setLoading] = useState(false);
        const [data, setData] = useState({
            drugs: new Array<Prescription>(),
            physician: new Array<Physician>(),
            physicianData: new Array<Physician>(),
        });

        useEffect(() => {
            if (parameterStore?.rxRecords) {
                setData(parameterStore.rxRecords);
            }
        }, [parameterStore?.rxRecords]);

        const [err, setErr] = useState('');

        /* eslint-enable */
        const GetRecords = async () => {
            setErr('');
            setShowConsentSuggestion(false);
            setLoading(true);
            // Ensure form is saved before requesting RX data
            await RootStore.agentPortalStore.contactsFormStore.saveForm('preapproval');
            await RootStore.agentPortalStore.contactsFormStore.saveForm('quote');
            await RootStore.agentPortalStore.contactsFormStore.saveForm('prequal');

            // get customer consent
            const getConsentData = {
                firstName: getParam('firstName'),
                lastName: getParam('lastName'),
                dob: getParam('dob'),
            };

            let valid = true,
                invalidKey = '';
            for (const [key, value] of Object.entries(getConsentData)) {
                if (!value) {
                    valid = false;
                    invalidKey = key;
                }
            }
            if (!valid) {
                RootStore.uiStore.showSnackbar(`${invalidKey} cannot be empty`, 'error');
                return;
            }

            const respConsent = await CRM.GetCustomerConsent(
                getConsentData.firstName,
                getConsentData.lastName,
                getConsentData.dob,
            );

            if (!respConsent?.consent) {
                setShowConsentSuggestion(true);
                setLoading(false);
                RootStore.uiStore.showSnackbar('Unavailable to fetch customer data: consent is missing', 'info');
                return;
            }

            const requestBody: RxRequest = {
                firstName: getParam('firstName'),
                lastName: getParam('lastName'),
                gender: getParam('gender'),
                dateOfBirth: getParam('dob'),
                zip: getParam('addressZipCode'),
                ssn: getParam('socialSecurityNumber'),
            };

            const makeRxCall = () => API.CallFullResponse('v2/rx/user-data', 'POST', requestBody);

            // response will be null if a timeout response without CORS header was received.
            let response: AxiosResponse<any> | null = await makeRxCall();

            const retryStatuses = [204, 503, 504];
            let retryCount = 0;
            while (!response || (retryStatuses.includes(response.status) && retryCount < RX_RETRY_COUNT_LIMIT)) {
                // eslint-disable-next-line no-await-in-loop
                await Utils.sleep(RX_FETCH_RETRY_WAIT_TIME);
                // eslint-disable-next-line no-await-in-loop
                response = await makeRxCall();
                retryCount += 1;
            }

            API.LogToMixPanel('agent-request-prescription', {
                firstname: getParam('firstName'),
                lastname: getParam('lastName'),
                gender: getParam('gender'),
                dob: getParam('dob'),
                agent: User.Info.email,
            });

            if (response?.status === 200) {
                const responseData: RxInitialResponse = response.data;
                const {rxData} = responseData;

                if (!rxData || rxData.physicianData.length < 1) {
                    setErr('Error fetching RX Data, physician data is empty');
                    setData({
                        drugs: new Array<Prescription>(),
                        physician: new Array<Physician>(),
                        physicianData: new Array<Physician>(),
                    });

                    setLoading(false);
                    return;
                }
                // modified rx response
                const modifiedResponseData: any = {...rxData};
                const agentStore = RootStore.agentPortalStore;
                const parameterStore = agentStore.parameterStore;
                if (parameterStore != null) {
                    const patientDrugs = new Array<Prescription>();
                    const drugSet = new Set<string>();

                    rxData?.rxDrugData.forEach((pd) => {
                        const drug = pd.rx.toUpperCase().trim();
                        const score = pd.score;
                        const cond = pd.cond || [];
                        if (drugSet.has(drug)) {
                            patientDrugs.find((d) => d.drug === drug)?.date.push(pd?.date);
                            return;
                        }

                        drugSet.add(drug);
                        patientDrugs.push({drug, score, date: [pd?.date], cond});
                    });

                    patientDrugs.sort((a, b) => b.score - a.score);
                    modifiedResponseData.drugs = patientDrugs;
                }

                runInAction(() => {
                    parameterStore!.rxRecords = modifiedResponseData;
                });

                setData(modifiedResponseData);
            } else {
                console.log('error', response?.data);
                setErr(response?.data || 'Error fetching RX Data');
                setData({
                    drugs: new Array<Prescription>(),
                    physician: new Array<Physician>(),
                    physicianData: new Array<Physician>(),
                });
            }

            setLoading(false);
        };

        const SendConsent = async () => {
            try {
                // form customer link
                const getConsentCodeData = {
                    firstName: getParam('firstName'),
                    lastName: getParam('lastName'),
                    dob: getParam('dob'),
                };
                const respCode = await CRM.GetCustomerConsentCode(
                    getConsentCodeData.firstName,
                    getConsentCodeData.lastName,
                    getConsentCodeData.dob,
                );
                const code = respCode?.code;
                if (!code) {
                    RootStore.uiStore.showSnackbar('Error during forming customer consent link', 'error');
                    return;
                }

                const link = `${CUSTOMER_PORTAL_URL}/consent-form/?c=${code}`;
                setConsentLink(link);
                setConsentEmail(getParam('emailAddress'));
                setConsentPhone(getParam('phoneNumber'));

                setShowConsentDialog(true);
            } catch (e) {
                RootStore.uiStore.showSnackbar('Error during forming customer consent link', 'error');
            }
        };

        const onCloseConsentDialog = async () => {
            setShowConsentDialog(false);
        };

        return (
            <Grid container spacing={3}>
                <Grid item xs={fullWidth ? 12 : 9}>
                    <Card style={{padding: 20}}>
                        <h4>RX Database</h4>
                        <Grid container spacing={3}>
                            <Grid item xs={4}>
                                <TextField
                                    variant="outlined"
                                    className={classes.formControl}
                                    label="First Name"
                                    value={getParam('firstName')}
                                    onChange={(e) => {
                                        setParam('firstName', e.target.value);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    variant="outlined"
                                    className={classes.formControl}
                                    label="Last Name"
                                    value={getParam('lastName')}
                                    onChange={(e) => {
                                        setParam('lastName', e.target.value);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <FormControl fullWidth variant="outlined" className={classes.formControl}>
                                    <InputLabel id={'gender'}>Gender</InputLabel>
                                    <Select
                                        labelId="gender"
                                        label="Gender"
                                        value={getParam('gender')}
                                        onChange={(e) => {
                                            setParam('gender', e.target.value as string);
                                        }}
                                    >
                                        <MenuItem value="Male">Male</MenuItem>
                                        <MenuItem value="Female">Female</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    variant="outlined"
                                    className={classes.formControl}
                                    label="Birth Date (YYYY-MM-DD)"
                                    value={getParam('dob')}
                                    onChange={(e) => {
                                        setParam('dob', e.target.value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    variant="outlined"
                                    className={classes.formControl}
                                    label="SSN (9 digits, no special chars)"
                                    value={getParam('socialSecurityNumber')}
                                    onChange={(e) => {
                                        setParam('socialSecurityNumber', e.target.value);
                                    }}
                                    inputProps={{
                                        autoComplete: 'new-password',
                                    }}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    variant="outlined"
                                    className={classes.formControl}
                                    label="Zip Code (5 Digit)"
                                    value={getParam('addressZipCode')}
                                    onChange={(e) => {
                                        setParam('addressZipCode', e.target.value);
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} style={{display: 'flex', justifyContent: 'space-between'}}>
                                <div>
                                    {showConsentSuggestion ? (
                                        <Button
                                            id="getRXSendContentDialogDisplayButton"
                                            variant="contained"
                                            color="primary"
                                            onClick={SendConsent}
                                        >
                                            Send consent link
                                        </Button>
                                    ) : (
                                        <span></span>
                                    )}
                                </div>

                                <div>
                                    {loading ? (
                                        <div style={{marginRight: 30}}>
                                            <span>loading... this may take upto 60 seconds...</span>
                                            <CircularProgress />
                                        </div>
                                    ) : (
                                        <Button
                                            id="getRXGetRXRecordsButton"
                                            variant="contained"
                                            color="primary"
                                            onClick={GetRecords}
                                            disabled={true}
                                        >
                                            Get RX Records
                                        </Button>
                                    )}
                                </div>
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                <Grid item xs={fullWidth ? 12 : 9}>
                    {loading === false || data?.drugs?.length ? (
                        <Card style={{padding: 20}}>
                            {err != '' ? (
                                <Alert severity="warning">{err}</Alert>
                            ) : (
                                <RxTable
                                    drugs={data?.drugs}
                                    physician={data?.physicianData != null ? data?.physicianData : data?.physician}
                                />
                            )}
                        </Card>
                    ) : null}
                </Grid>
                <RxDialog
                    open={showConsentDialog}
                    onClose={onCloseConsentDialog}
                    link={consentLink}
                    email={consentEmail}
                    setEmail={setConsentEmail}
                    phone={consentPhone}
                    setPhone={setConsentPhone}
                    type="consent"
                ></RxDialog>
            </Grid>
        );
    },
);

export default RXPanel;
